import _get from 'lodash/get'
import _set from 'lodash/set'
import _isEmpty from 'lodash/isEmpty'
import _isString from 'lodash/isString'
import _isArray from 'lodash/isArray'
import _isNil from 'lodash/isNil'
import _trim from 'lodash/trim'
import _isFunction from 'lodash/isFunction'
import _fpFlow from 'lodash/fp/flow'
import _fpMapValues from 'lodash/fp/mapValues'
import _fpOmitBy from 'lodash/fp/omitBy'
import { scroller } from 'react-scroll'
import moment from 'moment'
const cardValidator  = require('card-validator')

export const defaultValidators = {
  required(value) {
    const trimmedValue = _trim(value)
    if (_isNil(trimmedValue) || trimmedValue === '') {
      return 'common:form-error-required'
    }
    return null
  },
  email(value) {
    const regexp = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i
    return regexp.test(_trim(value)) ? null : 'common:form-error-invalid-email'
  },
  emailMaxLength(value) {
    if (value && value.length > 20) {
      return 'common:form-error-invalid-email-max-length'
    }
    return null
  },
  confirmEmailMatch: (value, allValues) => {
    const valueToCompare = allValues.username || allValues.user.username
    return (value && value !== valueToCompare) ? 'common:form-error-email-should-match' : undefined
  },
  password(value) {
    if (value && value.length < 6) {
      return 'common:form-error-invalid-password'
    }
    return null
  },
  confirmPasswordMatch: (value, allValues) => {
    const valueToCompare = allValues.password || allValues.user.password
    return (value && value !== valueToCompare) ? 'common:form-error-password-should-match' : undefined
  },
  phone(value) {
    const regexp = /\D*([2-9]\d{2})(\D*)([2-9]\d{2})(\D*)(\d{4})\D*/
    return regexp.test(_trim(value)) ? null : 'common:form-error-invalid-phone'
  },
  url(value) {
    const regexp = /^(http|https):\/\/[^ "]+$/
    const trimmedValue = _trim(value)
    if (!trimmedValue) {
      return null
    }
    return regexp.test(trimmedValue) ? null : 'common:form-error-invalid-url'
  },
  minLength: min => value =>
    value && value.length < min ? 'common:form-error-min-length' : undefined,
  maxLength: max => value =>
    value && value.length > max ? 'common:form-error-max-length' : undefined,
  length: length => value =>
    value && value.length !== length ? `common:form-error-length-${length}` : undefined,
  zip: value => {
    const regexp = /^(\d{5}(-\d{4})?|[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d)$/
    const trimmedValue = _trim(value)
    if (!trimmedValue) {
      return null
    }
    return regexp.test(trimmedValue) ? null : 'common:form-error-invalid-zip'
  },
  creditCard: value => {
    const numberValidation = cardValidator.number(value)
    return numberValidation.isValid ? null : 'common:form-error-invalid-credit-card-number'
  },
  creditCardType: value => {
    const allowedTypes = ['visa', 'mastercard', 'american-express', 'discover']
    const numberValidation = cardValidator.number(value)
    const isCardValid = numberValidation.isValid
    const cardType = numberValidation.card.type
    const isTypeValid = allowedTypes.includes(cardType)
    return isCardValid && isTypeValid ? null : 'common:form-error-invalid-credit-card-type'
  },
  cvv: (value, allValues ) => {
    const cardInfo = cardValidator.number(allValues.card_number)
    let maxLength = 3

    if (cardInfo.card && cardInfo.card.isAmex) {
      maxLength = 4
    }

    const numberValidation = cardValidator.cvv(value, maxLength)
    return numberValidation.isValid ? null : 'common:form-error-invalid-cvv'
  },
  cardExpiration: (value, allValues) => {
    const cardYear = allValues.card_year

    if (!cardYear) {
      return null
    }

    const today = moment()
    const dateToValidate = moment()
      .date(today.date())
      .month(+value - 1)
      .year(`20${cardYear}`)

    return dateToValidate.isBefore(today) ? 'common:form-error-invalid-exp-date' : null
  },
  vin: value => {
    const regexp = /^[A-HJ-NPR-Z\d]{3}[A-HJ-NPR-Z\d]{5}[\dX][A-HJ-NPR-Z\d][A-HJ-NPR-Z\d][A-HJ-NPR-Z\d]{6}$/
    const trimmedValue = _trim(value).toUpperCase()
    return regexp.test(trimmedValue) ? null : 'common:form-error-invalid-vin'
  },
  price: value => {
    const regexp = /(\d+\.\d{1,2})/
    const trimmedValue = _trim(value)
    return regexp.test(trimmedValue) ? null : 'common:form-error-invalid-price'
  }
}

export const validateSync = (values, config) => (_fpFlow(
  _fpMapValues.convert({ cap: false })((validationType, fieldName) => {
    const value = _get(values, fieldName)

    if (_isString(validationType)) {
      return defaultValidators[validationType](value)
    }

    if (_isFunction(validationType)) {
      return validationType(value, values)
    }

    if (_isArray(validationType)) {
      return validationType.reduce((prevValidation, innerValidation) =>
        prevValidation || validateSync(values, {
          [fieldName]: innerValidation,
        })[fieldName]
        , undefined)
    }
    throw new Error()
  }),
  _fpOmitBy(_isNil)
))(config)

export const validateAsync = (values, config) => {
  const errors = validateSync(values, config)
  return _isEmpty(errors) ? Promise.resolve() : Promise.reject(errors)
}

export const scrollToFirstError = errors => {
  const flattenedObj = flattenObject(errors)
  const errorFields = Object.keys(flattenedObj)
    .filter(item => item !== '_error')

  if (errorFields.length) {
    const el = document.querySelector(`[name="${errorFields[0]}"]`)

    if (el) {
      scroller.scrollTo(`${errorFields[0]}`, {
        duration: 1000,
        delay: 0,
        smooth: 'easeInOutQuart'
      })
    }
  }
}

const flattenObject = function(ob) {
	const toReturn = {}

	for (let i in ob) {
		if (!ob.hasOwnProperty(i)) continue

		if ((typeof ob[i]) == 'object') {
			const flatObject = flattenObject(ob[i])
			for (var x in flatObject) {
				if (!flatObject.hasOwnProperty(x)) continue

				toReturn[i + '.' + x] = flatObject[x]
			}
		} else {
			toReturn[i] = ob[i]
		}
	}
	return toReturn
}

export const reduxErrorFormatter = ({ errors }) => {
  const errObj = {}
  for (const key in errors) {
    if (errors.hasOwnProperty(key)) {
      const err = errors[key]
      _set(errObj, key, err.error)
    }
  }
  return errObj
}



// WEBPACK FOOTER //
// ./lib/validation.js